import type { Injector } from '@angular/core';
import { inject, runInInjectionContext } from '@angular/core';
import { MatDialog, type MatDialogRef } from '@angular/material/dialog';
import { assertInjector } from '@quipex/core/utils';
import type { DialogResult } from '../dialog';
import { registerDialogLifecycle } from '../dialog/register-dialog-lifecycle';
import { FilePreviewDialog } from './file-preview-dialog';
import type {
    FilePreviewAction,
    FilePreviewDialogData,
    FilePreviewInfoPanelConfig,
    FilePreviewPanelConfig,
    FilePreviewPanelContentFactory,
} from './models';

export interface FilePreviewDialogOptions {
    previewUrl: string | null | undefined;
    fileName: string;
    ariaLabel?: string;
    initialPage?: number;
    searchTerm?: string;
    actions?: readonly FilePreviewAction[];
    panels?: readonly FilePreviewPanelConfig[];
    infoPanel?: FilePreviewInfoPanelConfig;
    panelContentFactory?: FilePreviewPanelContentFactory;
}

export interface FilePreviewDialogLauncher {
    open: (
        options: FilePreviewDialogOptions
    ) => MatDialogRef<FilePreviewDialog, DialogResult>;
}

export function injectFilePreviewDialog(
    injector?: Injector
): FilePreviewDialogLauncher {
    const resolvedInjector = assertInjector(injectFilePreviewDialog, injector);

    const open = (
        options: FilePreviewDialogOptions
    ): MatDialogRef<FilePreviewDialog, DialogResult> => {
        return runInInjectionContext(resolvedInjector, () => {
            const matDialog = inject(MatDialog);

            const ref = matDialog.open<
                FilePreviewDialog,
                FilePreviewDialogData,
                DialogResult
            >(FilePreviewDialog, {
                panelClass: ['qpx-dialog', 'xl', 'no-padding'],
                injector: resolvedInjector,
                ariaLabel: options.ariaLabel ?? `Preview ${options.fileName}`,
                data: {
                    ariaLabel: options.ariaLabel,
                    previewUrl: options.previewUrl,
                    fileName: options.fileName,
                    initialPage: options.initialPage,
                    searchTerm: options.searchTerm,
                    actions: options.actions,
                    panels: options.panels,
                    infoPanel: options.infoPanel,
                    panelContentFactory: options.panelContentFactory,
                } satisfies FilePreviewDialogData,
                autoFocus: false,
                restoreFocus: false,
            });
            registerDialogLifecycle(ref, resolvedInjector);
            return ref;
        });
    };

    return { open };
}
